import React from 'react';

/**
 * Per-member placement for a MongoDB sharded cluster.
 *
 * The generic master+slaves renderer cannot express this shape: there are three different
 * node types (shard members, config servers, routers), and the shard members are a grid of
 * shards × members. So this section replaces it whenever a variant sets `mongo_topology`.
 *
 * Hostnames are typed, not chosen from a list of available capacity — the DBA checks what a
 * node has free themselves, which is what they asked for. Blank means "let Kubernetes place
 * it", and the backend then applies anti-affinity so two members of the same shard avoid
 * landing on one node.
 */
const S = {
  wrap: {
    border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16,
    background: 'var(--surface-2)',
  },
  title: { fontWeight: 700, fontSize: 13, marginBottom: 4 },
  hint: { fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 },
  group: { marginBottom: 14 },
  groupLabel: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    color: 'var(--muted)', marginBottom: 6,
  },
  row: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
  tag: { width: 132, fontSize: 12, color: 'var(--ink-2)' },
  input: {
    padding: '5px 10px', width: 190, borderRadius: 6, fontSize: 13,
    border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--ink-2)',
  },
  pill: (bg, fg) => ({
    display: 'inline-block', padding: '1px 7px', borderRadius: 20, fontSize: 10,
    fontWeight: 700, background: bg, color: fg, textTransform: 'uppercase',
    letterSpacing: 0.4,
  }),
  warn: {
    fontSize: 12, color: 'var(--ink-2)', background: 'rgba(217,119,6,0.10)',
    border: '1px solid var(--color-warning-500)', borderRadius: 8, padding: '10px 12px',
    marginBottom: 12, lineHeight: 1.5,
  },
};

const ARBITER_PILL = S.pill('rgba(217,119,6,0.18)', 'var(--color-warning-500)');
const DATA_PILL = S.pill('rgba(16,185,129,0.15)', 'var(--color-success-500)');

export default function MongoTopology({ form, setForm, nodeTaints = {} }) {
  const shardCount = Math.max(1, Number(form.shard_count ?? 2));
  const members = Number(form.members_per_shard ?? 3);
  const cfgCount = Number(form.config_count ?? 3);
  const routers = Math.max(1, Number(form.router_count ?? 2));
  const useArbiter = !!form.use_arbiter;
  // Manual storage mode reuses THESE SAME hostname fields as the manual-PV node (see
  // catalog_storage.resolve_member_storage's node_override param) — blank is no longer "let
  // Kubernetes place it" in that mode, it's a hard error (ManualPvMisconfigured) for every
  // shard/config member (routers are stateless, no storage, so they're unaffected).
  const manualStorage = form.pv_mode === 'manual';

  // shard_nodes is a grid: one row per shard, one entry per member.
  const shardNodes = form.shard_nodes || [];
  const cfgNodes = form.config_nodes || [];
  const routerNodes = form.router_nodes || [];

  const setShardNode = (s, m, value) => {
    const grid = shardNodes.map(r => [...(r || [])]);
    while (grid.length < shardCount) grid.push([]);
    while (grid[s].length < members) grid[s].push('');
    grid[s][m] = value;
    setForm({ ...form, shard_nodes: grid });
  };

  const setListNode = (field, list, i, value) => {
    const next = [...list];
    while (next.length <= i) next.push('');
    next[i] = value;
    setForm({ ...form, [field]: next });
  };

  const dataPerShard = useArbiter ? members - 1 : members;

  // Every hostname typed here (dynamic or manual-PV) becomes a direct nodeSelector — a
  // typed-in tainted node has the same "silently stuck Pending" risk a manual PV pin has
  // anywhere else in the catalog. Free-text here (not a dropdown, by this component's own
  // design — see module docstring), so this can only warn on a value the operator already
  // typed, not prevent choosing one in the first place.
  const taintNote = (nodeName) => {
    const taints = nodeTaints[(nodeName || '').trim()];
    if (!taints || !taints.length) return null;
    return `⚠ Tainted: ${taints.map(t => `${t.key}=${t.value || ''}`).join(', ')}`;
  };

  return (
    <div style={S.wrap}>
      <div style={S.title}>Cluster topology &amp; placement</div>
      <div style={S.hint}>
        Enter a node hostname to pin a member, or leave blank to let Kubernetes place it —
        blank members of the same shard are automatically kept off the same node.
      </div>

      {manualStorage && (
        <div style={S.warn}>
          <strong>Storage mode is Manual.</strong> Every shard member and config server below
          needs a node filled in — SarvKube creates that member's storage directly on the node
          you enter here, so blank is no longer allowed for them (mongos routers have no
          storage and can stay blank).
        </div>
      )}

      {/* Stating the durability consequence where the decision is made, not in a doc. */}
      {useArbiter && (
        <div style={S.warn}>
          <strong>Arbiter enabled — {dataPerShard} copies of data per shard.</strong> An arbiter
          votes in elections but stores nothing. Losing one data node leaves this shard with no
          redundancy, and <code>w:majority</code> writes (transactions, change streams) stall
          while it is down. MongoDB advises against arbiters in production.
        </div>
      )}

      <div style={S.group}>
        <div style={S.groupLabel}>
          Shards — {shardCount} × {members} members ({dataPerShard} data-bearing each)
        </div>
        {Array.from({ length: shardCount }, (_, s) => (
          <div key={s} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Shard {s + 1}</div>
            {Array.from({ length: members }, (_, m) => {
              const isArb = useArbiter && m === members - 1;
              const note = taintNote((shardNodes[s] || [])[m]);
              return (
                <div key={m} style={{ marginBottom: 6 }}>
                  <div style={S.row}>
                    <span style={S.tag}>
                      {m === 0 ? 'Member 1 (primary)' : `Member ${m + 1}`}
                    </span>
                    <input
                      style={S.input}
                      placeholder={manualStorage ? '(required — node for this member\'s storage)' : '(any node)'}
                      value={(shardNodes[s] || [])[m] ?? ''}
                      onChange={e => setShardNode(s, m, e.target.value)}
                    />
                    <span style={isArb ? ARBITER_PILL : DATA_PILL}>
                      {isArb ? 'arbiter · no data' : 'data'}
                    </span>
                  </div>
                  {note && (
                    <div style={{ fontSize: 11, color: 'var(--color-warning-500)', marginLeft: 140 }}>{note}</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={S.group}>
        <div style={S.groupLabel}>
          Config servers — {cfgCount} (holds the shard map; the cluster stops if this loses quorum)
        </div>
        {Array.from({ length: cfgCount }, (_, i) => {
          const note = taintNote(cfgNodes[i]);
          return (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={S.row}>
                <span style={S.tag}>Config {i + 1}</span>
                <input
                  style={S.input}
                  placeholder={manualStorage ? '(required — node for this member\'s storage)' : '(any node)'}
                  value={cfgNodes[i] ?? ''}
                  onChange={e => setListNode('config_nodes', cfgNodes, i, e.target.value)}
                />
              </div>
              {note && (
                <div style={{ fontSize: 11, color: 'var(--color-warning-500)', marginLeft: 140 }}>{note}</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={S.group}>
        <div style={S.groupLabel}>
          mongos routers — {routers} (clients connect through these)
        </div>
        {Array.from({ length: routers }, (_, i) => (
          <div key={i} style={S.row}>
            <span style={S.tag}>Router {i + 1}</span>
            <input
              style={S.input}
              placeholder="(any node)"
              value={routerNodes[i] ?? ''}
              onChange={e => setListNode('router_nodes', routerNodes, i, e.target.value)}
            />
          </div>
        ))}
      </div>

      <div style={{ ...S.hint, marginBottom: 0 }}>
        Total pods: {shardCount * members} shard members + {cfgCount} config + {routers} routers
        {' '}= <strong>{shardCount * members + cfgCount + routers}</strong>
      </div>
    </div>
  );
}
